import React, { useEffect, useState } from "react";
import { Plus, Trash2, Link2, ExternalLink, MessageSquareText, Bell, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ClosingDashboard } from "./ClosingDashboard";
import { CommentField, FieldCommentsProvider } from "./CommentField";

const isUrl = (s) => /^https?:\/\//.test(s || "");
export const OUTCOMES = [
  { key: "close", label: "Closé" },
  { key: "pas_close", label: "Pas closé" },
  { key: "en_attente", label: "En attente" },
];
const LABEL = "text-sm font-medium normal-case tracking-normal";
const CARD = "rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6";
const FIELD = "w-full rounded-2xl border border-line bg-cream/60 px-4 py-2.5 font-sans text-sm font-light text-ink outline-none focus:border-bordeaux";
const RICH = "min-h-[96px] w-full rounded-2xl border border-line bg-white p-4 font-sans text-sm font-light leading-relaxed text-ink focus:border-bordeaux";
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "");

function Header({ s, bind, onDelete, open, onToggle }) {
  const outcome = OUTCOMES.find((o) => o.key === s.outcome);
  return (
    <div className="rounded-2xl bg-[#590224] px-5 py-4 text-white shadow-[0_8px_24px_rgba(89,2,36,0.22)] sm:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[#F3D2D9]">Prénom du prospect</p>
          <input data-testid={`closing-prospect-${s.id}`} {...bind("prospect")} placeholder="Ex : Camille"
            className="mt-0.5 w-full bg-transparent font-sans text-lg font-medium text-white outline-none placeholder:font-light placeholder:text-white/40" />
        </div>
        <div className="w-20 shrink-0">
          <p className="text-sm font-medium text-[#F3D2D9]">Appel n°</p>
          <input data-testid={`closing-number-${s.id}`} {...bind("call_number")} placeholder="1"
            className="mt-0.5 w-full bg-transparent font-sans text-lg font-medium text-white outline-none placeholder:font-light placeholder:text-white/40" />
        </div>
        <button data-testid={`closing-delete-${s.id}`} onClick={() => onDelete(s)} title="Supprimer la section"
          className="shrink-0 rounded-full p-1.5 text-white/50 transition hover:bg-white/10 hover:text-white"><Trash2 size={15} /></button>
        <button data-testid={`closing-toggle-${s.id}`} onClick={onToggle} title={open ? "Refermer" : "Ouvrir"}
          className="shrink-0 rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"><ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {!open && (s.call_date || outcome || s.feedback) && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
          {s.call_date && <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-white/80">{fmtDate(s.call_date)}</span>}
          {outcome && <span className="rounded-full bg-white px-2.5 py-0.5 font-medium text-[#590224]">{outcome.label}</span>}
          {s.feedback && <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-white/80">Feedback reçu ✨</span>}
        </div>
      )}
    </div>
  );
}

function CallInfo({ s, f, bind, setOutcome, isAdmin, rich }) {
  return (
    <div className={`${CARD} grid gap-4 sm:grid-cols-2`}>
      <div>
        <p className={`${LABEL} text-bordeaux`}>Date de l'appel</p>
        <input data-testid={`closing-date-${s.id}`} type="date" lang="fr" {...bind("call_date")} className={`${FIELD} mt-2`} />
      </div>
      <div>
        <p className={`${LABEL} text-bordeaux`}>Montant de l'offre (€)</p>
        <input data-testid={`closing-amount-${s.id}`} inputMode="decimal" {...bind("amount")} placeholder="Ex : 1500" className={`${FIELD} mt-2`} />
      </div>
      <div className="sm:col-span-2">
        <p className={`${LABEL} text-bordeaux`}>Résultat de l'appel</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {OUTCOMES.map((o) => (
            <button key={o.key} data-testid={`closing-outcome-${o.key}-${s.id}`} onClick={() => setOutcome(f.outcome === o.key ? "" : o.key)}
              className={`rounded-full px-4 py-2 text-xs font-medium transition ${f.outcome === o.key ? "bg-bordeaux text-white shadow-[0_0_16px_rgba(123,17,58,0.4)]" : "border border-line bg-white text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2">
        <p className={`${LABEL} text-bordeaux`}>{isAdmin ? "Son ressenti sur cet appel" : "Mon ressenti sur cet appel"}</p>
        <div className="mt-2"><CommentField testid={`closing-note-${s.id}`} fieldKey={`closing-note-${s.id}`} value={s.client_note || ""} {...rich("client_note")}
          placeholder={isAdmin ? "Pas encore de ressenti partagé." : "Comment ça s'est passé ? Ce qui a bien marché, là où tu as bloqué, les objections…"} className={RICH} /></div>
      </div>
    </div>
  );
}

function LinkBox({ s, f, isAdmin, bind, notify, sending }) {
  return (
    <div className="rounded-2xl border border-bordeaux-border bg-white p-5 shadow-card sm:p-6">
      <p className={`${LABEL} text-bordeaux`}>{isAdmin ? "Le lien de l'appel" : "Le lien de ton appel"}</p>
      <p className="mt-1 text-sm font-light text-ink-muted">{isAdmin ? "L'enregistrement de l'appel partagé par ta cliente." : "Colle ici l'enregistrement de ton appel pour que je puisse l'analyser."}</p>
      <div className="mt-3 flex items-center gap-2 rounded-full border border-line bg-cream/60 px-4 focus-within:border-bordeaux">
        <Link2 size={15} className="shrink-0 text-bordeaux" />
        <input data-testid={`closing-link-${s.id}`} type="url" {...bind("link")} placeholder="https://..."
          className="w-full bg-transparent py-2.5 text-sm font-light text-ink outline-none placeholder:text-ink-muted" />
      </div>
      {!isAdmin && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <button data-testid={`closing-notify-${s.id}`} onClick={notify} disabled={sending || !f.link}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.35)] transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
            {s.notified_at ? <Check size={15} /> : <Bell size={15} />}{s.notified_at ? "Prévenir à nouveau" : "Prévenir Fanny pour qu'elle analyse mon appel"}
          </button>
          {s.notified_at && <p className="text-xs font-light text-ink-muted">Fanny a été prévenue, elle analyse ton appel ✨</p>}
        </div>
      )}
      {isAdmin && (
        <p data-testid={`closing-notified-${s.id}`} className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${s.notified_at ? "bg-bordeaux text-white" : "bg-cream text-ink-muted"}`}>
          <Bell size={12} />{s.notified_at ? `Ta cliente t'a prévenue le ${new Date(s.notified_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}` : "Ta cliente ne t'a pas encore prévenue"}
        </p>
      )}
      {isUrl(s.link) && (
        <a data-testid={`closing-link-open-${s.id}`} href={s.link} target="_blank" rel="noreferrer" className="mt-3 flex w-fit items-center gap-1.5 text-xs font-medium text-bordeaux hover:underline">
          Ouvrir l'appel <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
}

function Section({ s, base, isAdmin, onSaved, onDelete, defaultOpen }) {
  const [f, setF] = useState(s);
  const [open, setOpen] = useState(defaultOpen);
  const [sending, setSending] = useState(false);
  useEffect(() => { setF(s); }, [s.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const put = async (patch) => {
    try {
      const { data } = await api.put(`${base}/${s.id}`, patch);
      onSaved(data);
      toast.success("Enregistré ✨");
    } catch (_) {
      toast.error("Oups, réessaie dans un instant.");
    }
  };
  const save = (key) => { if ((f[key] || "") !== (s[key] || "")) put({ [key]: f[key] }); };
  const bind = (key) => ({ value: f[key] || "", onChange: (e) => { const v = e.target.value; setF((p) => ({ ...p, [key]: v })); }, onBlur: () => save(key) });
  const rich = (key) => ({ onChange: (html) => setF((p) => ({ ...p, [key]: html })), onBlur: (html) => { if (html !== (s[key] || "")) put({ [key]: html }); } });
  const setOutcome = (v) => { setF({ ...f, outcome: v }); put({ outcome: v }); };
  const notify = async () => {
    setSending(true);
    try {
      if ((f.link || "") !== (s.link || "")) await api.put(`${base}/${s.id}`, { link: f.link });
      const { data } = await api.post(`${base}/${s.id}/notify`);
      onSaved(data);
      toast.success("Fanny a bien été prévenue 🎉");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oups, réessaie dans un instant.");
    } finally {
      setSending(false);
    }
  };
  return (
    <section data-testid={`closing-section-${s.id}`} className="space-y-3">
      <Header s={s} bind={bind} onDelete={onDelete} open={open} onToggle={() => setOpen(!open)} />
      {open && <>
      <CallInfo s={s} f={f} bind={bind} setOutcome={setOutcome} isAdmin={isAdmin} rich={rich} />
      <LinkBox s={s} f={f} isAdmin={isAdmin} bind={bind} notify={notify} sending={sending} />
      <div className="rounded-2xl border border-line bg-bordeaux-tint/30 p-5 sm:p-6">
        <p className={`flex items-center gap-2 ${LABEL} text-bordeaux`}><MessageSquareText size={14} /> Feedback de ma coach</p>
        {isAdmin ? (
          <div className="mt-3"><CommentField testid={`closing-feedback-${s.id}`} fieldKey={`closing-feedback-${s.id}`} value={s.feedback || ""} {...rich("feedback")}
            placeholder="Écris ton retour sur cet appel…" className={`${RICH} min-h-[320px]`} /></div>
        ) : s.feedback ? (
          <div className="mt-3"><CommentField readOnly testid={`closing-feedback-${s.id}`} fieldKey={`closing-feedback-${s.id}`} value={s.feedback} className="min-h-[320px] break-words rounded-2xl bg-white p-4 text-sm font-light leading-relaxed text-ink-soft" /></div>
        ) : (
          <p data-testid={`closing-feedback-${s.id}`} className="mt-3 min-h-[320px] rounded-2xl bg-white p-4 text-sm italic leading-relaxed text-ink-muted">
            {s.notified_at ? "Fanny analyse ton appel, son retour arrive ici ✨" : "Ajoute le lien de ton appel et préviens Fanny, elle t'écrira son retour ici."}
          </p>
        )}
      </div>
      </>}
    </section>
  );
}

export function ClosingFeedbacks({ clientId = null }) {
  const isAdmin = !!clientId;
  const base = isAdmin ? `/admin/clients/${clientId}/closing-feedbacks` : "/client/closing-feedbacks";
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(base).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [base]);
  const [newId, setNewId] = useState(null);
  const add = async () => {
    const { data } = await api.post(base);
    setNewId(data.id);
    setItems((l) => [...(l || []), data]);
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer la section « ${s.prospect || "sans prénom"} — appel n°${s.call_number} » ?`)) return;
    await api.delete(`${base}/${s.id}`);
    setItems((l) => l.filter((x) => x.id !== s.id));
    toast.success("Section supprimée");
  };
  const saved = (d) => setItems((l) => l.map((x) => (x.id === d.id ? d : x)));
  if (!items) return null;
  return (
    <FieldCommentsProvider page="hub-closing" clientId={clientId}>
    <div className="space-y-8 font-sans">
      <ClosingDashboard items={items} />
      {items.length === 0 && (
        <p className="rounded-2xl border border-dashed border-line bg-white/60 p-8 text-center text-sm text-ink-muted">Aucun appel pour l'instant. Ajoute ta première section ✨</p>
      )}
      {items.map((s, i) => <Section key={s.id} s={s} base={base} isAdmin={isAdmin} onSaved={saved} onDelete={remove} defaultOpen={s.id === newId || (!newId && i === items.length - 1)} />)}
      <button data-testid="closing-add-section" onClick={add}
        className="flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-bordeaux-border bg-white py-3 text-sm font-medium text-bordeaux transition hover:bg-bordeaux-tint/40">
        <Plus size={16} /> Ajouter une autre section
      </button>
    </div>
    </FieldCommentsProvider>
  );
}
